import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { isContentWritingAction } from "../auditUtils";
import {
	getWritingAssistantRecommendations,
	submitWritingAssistantJob,
} from "../../../services/writingAssistantApi";
import {
	findPageByUrl,
	getPageJobId,
	normalizePageUrl,
	normalizeRecommendationId,
} from "./writingAssistantUtils";

export default function useWritingPreviewAction({ websiteId, auditId }) {
	const navigate = useNavigate();
	const [recMap, setRecMap] = useState(new Map());
	const [loadingRecs, setLoadingRecs] = useState(false);
	const [submittingKey, setSubmittingKey] = useState(null);

	useEffect(() => {
		if (websiteId == null || auditId == null) {
			setRecMap(new Map());
			return;
		}

		let cancelled = false;

		const fetchRecs = async () => {
			setLoadingRecs(true);
			const result = await getWritingAssistantRecommendations(websiteId, auditId);
			if (cancelled) return;

			if (!result.ok) {
				setRecMap(new Map());
			} else {
				const map = new Map();
				(result.recommendations || []).forEach((rec) => {
					const key = normalizeRecommendationId(rec.id);
					if (key != null) map.set(key, rec);
				});
				setRecMap(map);
			}
			setLoadingRecs(false);
		};

		fetchRecs();

		return () => {
			cancelled = true;
		};
	}, [websiteId, auditId]);

	const getRec = useCallback(
		(recommendationId) => recMap.get(normalizeRecommendationId(recommendationId)) ?? null,
		[recMap]
	);

	const getPageJobIdForRec = useCallback(
		(recommendationId, pageUrl) => {
			const rec = getRec(recommendationId);
			if (!rec || !pageUrl) return null;
			const page = findPageByUrl(rec.affected_pages, pageUrl);
			return getPageJobId(page);
		},
		[getRec]
	);

	const getPreviewActionState = useCallback(
		(recommendationId, pageUrl) => {
			const normalizedUrl = normalizePageUrl(pageUrl);
			const existingJobId = getPageJobIdForRec(recommendationId, normalizedUrl);
			const rec = getRec(recommendationId);
			const hasJob = existingJobId != null;
			const processedWithoutJob = rec?.processed === true && !hasJob;

			return {
				label: hasJob ? "View Action Items" : "Generate Fix",
				canSubmit: !processedWithoutJob,
			};
		},
		[getPageJobIdForRec, getRec]
	);

	const canPreviewAction = useCallback(
		(action) => {
			if (!websiteId || auditId == null) return false;
			if (action?.type !== "recommendation") return false;
			if (!isContentWritingAction(action)) return false;
			const rec = getRec(action.id);
			if (!rec || rec.selectable === false) return false;
			if (rec.processed) {
				const hasAnyJob = rec.affected_pages?.some((p) => getPageJobId(p) != null);
				if (!hasAnyJob) return false;
			}
			return true;
		},
		[websiteId, auditId, getRec]
	);

	const submitOrViewPreview = useCallback(
		async ({ recommendationId, url }) => {
			if (!websiteId) {
				toast.error("Website not selected. Please select a website and try again.");
				return;
			}
			if (auditId == null) {
				toast.error("Audit not available for this action.");
				return;
			}
			if (recommendationId == null) {
				toast.error("Recommendation not found.");
				return;
			}
			if (!url) {
				toast.error("Page URL is required.");
				return;
			}

			const normalizedUrl = normalizePageUrl(url);
			const key = `${normalizeRecommendationId(recommendationId)}|${normalizedUrl}`;
			const existingJobId = getPageJobIdForRec(recommendationId, normalizedUrl);

			if (existingJobId != null) {
				navigate(
					`/aivisibility/writing-assistance/view?jobId=${encodeURIComponent(existingJobId)}`
				);
				return;
			}

			const rec = getRec(recommendationId);
			if (rec?.processed) {
				toast.info("This recommendation has already been processed.");
				return;
			}

			setSubmittingKey(key);

			try {
				const submit = await submitWritingAssistantJob(websiteId, {
					auditId,
					recommendationId,
					url: normalizedUrl,
				});

				if (!submit.ok || !submit.job?.id) {
					toast.error(submit.message || "Failed to submit job");
					return;
				}

				navigate(
					`/aivisibility/writing-assistance/view?jobId=${encodeURIComponent(submit.job.id)}`
				);
			} catch {
				toast.error("Failed to generate preview");
			} finally {
				setSubmittingKey(null);
			}
		},
		[websiteId, auditId, getPageJobIdForRec, getRec, navigate]
	);

	const isReady = useMemo(
		() => websiteId != null && auditId != null && !loadingRecs,
		[websiteId, auditId, loadingRecs]
	);

	return {
		canPreviewAction,
		getPageJobIdForRec,
		getPreviewActionState,
		submitOrViewPreview,
		submittingKey,
		loadingRecs,
		isReady,
	};
}
